import type { Trip, TripIntervenants } from "@/entities/trip";
import { sanitizeContactPhone } from "./contact";

/**
 * Les trois intervenants d'une course — §1 du contrat du 2026-08-26.
 *
 * Module PUR → testable sans mock.
 *
 * ## Ce que ce bloc remplace
 * Les trois personnes existaient déjà, mais éparpillées dans trois adresses
 * différentes : le contact du point de retrait vivait dans
 * `stops[].location`, celui du destinataire dans `delivery_address` ou dans
 * l'adresse d'un article, et la Main s'obtenait par un `GET /agents/{id}`
 * séparé. Chaque écran les recomposait à sa façon, et l'un d'eux oubliait
 * toujours un cas.
 *
 * Le backend rend désormais `intervenants` sur TOUTE course, sans paramètre à
 * passer. Ce module en fait une liste prête à afficher — et surtout, il traite
 * les deux absences qui sont NORMALES et non des pannes :
 *
 *   1. Une Main qui n'a reçu qu'une OFFRE, sans l'avoir acceptée, voit ces
 *      contacts vides. C'est voulu : la rotation peut solliciter plusieurs
 *      Mains, et diffuser le nom et le numéro du destinataire à des gens qui ne
 *      serviront jamais la course n'a aucune raison d'être.
 *
 *   2. `main.telephone` sera souvent `null`, et durablement : le numéro d'une
 *      Main n'a aucune source dans la plateforme aujourd'hui. L'affichage doit
 *      donc TENIR sans numéro — pas laisser un bouton d'appel dans le vide.
 */

export type IntervenantRole = "retrait" | "main" | "destinataire";

export type IntervenantView = {
  role:      IntervenantRole;
  /** Intitulé de la ligne, adapté à qui regarde l'écran. */
  title:     string;
  nom:       string | null;
  /** Numéro NETTOYÉ, ou `null` s'il est absent ou inexploitable. */
  telephone: string | null;
  adresse:   string | null;
  /** Identifiant de la Main — toujours présent quand le bloc `main` existe. */
  agentId:   string | null;
  /** Rien à montrer : offre non encore acceptée, ou donnée pas encore saisie. */
  isEmpty:   boolean;
};

/** Qui regarde — le même intervenant ne se nomme pas pareil des deux côtés. */
export type IntervenantAudience = "client" | "agent";

const TITLES: Record<IntervenantAudience, Record<IntervenantRole, string>> = {
  client: {
    retrait:      "Point de retrait",
    main:         "Votre Main",
    destinataire: "Destinataire",
  },
  agent: {
    retrait:      "Chez qui retirer",
    // L'agent EST la Main : lui afficher « Votre Main » n'aurait aucun sens.
    main:         "Vous",
    destinataire: "À qui remettre",
  },
};

/** Chaîne non vide, détourée — sinon `null`. Jamais `""` ni `"null"`. */
function text(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

const ORDER: IntervenantRole[] = ["retrait", "main", "destinataire"];

/**
 * Les trois intervenants, dans l'ordre du trajet : chez qui on prend, qui
 * porte, à qui on remet.
 *
 * Rend TOUJOURS trois entrées, même vides. C'est l'appelant qui décide de
 * masquer ou non — et la plupart des écrans gagnent à garder la structure
 * visible : le client sait où le nom apparaîtra dès qu'une Main acceptera,
 * plutôt que de voir une ligne surgir et tout décaler.
 */
export function viewIntervenants(
  intervenants: TripIntervenants | undefined | null,
  audience: IntervenantAudience = "client",
): IntervenantView[] {
  return ORDER.map((role) => {
    const raw = intervenants?.[role] ?? null;
    const nom = text(raw?.nom);
    // On passe par le nettoyage de contact : un numéro que le backend n'aurait
    // pas normalisé (« +229-97-00-00-00 ») doit rester composable, et un « + »
    // seul ne doit pas produire un lien d'appel qui ne mène nulle part.
    const telephone = sanitizeContactPhone(raw?.telephone) ?? null;
    const adresse = text(raw?.adresse);
    const agentId =
      role === "main" ? text((raw as { agent_id?: string | null } | null)?.agent_id) : null;

    return {
      role,
      title: TITLES[audience][role],
      nom,
      telephone,
      adresse,
      agentId,
      // `agentId` ne compte PAS comme un contenu : une Main identifiée mais sans
      // nom ni numéro n'a rien à montrer à un humain.
      isEmpty: nom == null && telephone == null && adresse == null,
    };
  });
}

/** Raccourci depuis une course. */
export function tripIntervenants(
  trip: Trip | undefined | null,
  audience: IntervenantAudience = "client",
): IntervenantView[] {
  return viewIntervenants(trip?.intervenants, audience);
}

/**
 * Le bloc a-t-il quoi que ce soit à montrer ?
 *
 * Sert à décider d'afficher la section ou non sur les écrans où elle serait du
 * bruit — typiquement une course qui vient d'être créée, avant toute affectation.
 */
export function hasAnyIntervenant(views: IntervenantView[]): boolean {
  return views.some((v) => !v.isEmpty);
}

/**
 * Lien d'appel, ou `undefined` si le numéro manque.
 *
 * Les espaces sont retirés : `tel:+229 97 00 00 00` est mal interprété par
 * certains composeurs, qui s'arrêtent au premier blanc.
 */
export function telHref(phone: string | null | undefined): string | undefined {
  if (!phone) return undefined;
  const compact = phone.replace(/\s/g, "");
  return compact.length > 0 ? `tel:${compact}` : undefined;
}
